// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  averageValues,
} from "../core/math.js";
import {
  engineMetricValue,
} from "../core/engines.js";
import {
  clampScore,
  quantile,
} from "./utils.js";
import {
  crossEngineParameters,
} from "./detectors/cross-engine.js";

const crossEngineFinite = (value) => Number.isFinite(value);

function formatNumber(value, unit = "", digits = 0) {
  if (!crossEngineFinite(value)) return "--";
  return `${value.toFixed(digits)}${unit}`;
}

function scoreForDelta(value, thresholds) {
  if (!crossEngineFinite(value)) return null;
  if (value >= thresholds.review) return crossEngineParameters.scores.review;
  if (value >= thresholds.watch) return crossEngineParameters.scores.watch;
  if (value >= thresholds.monitor) return crossEngineParameters.scores.monitor;
  return crossEngineParameters.scores.aligned;
}

function severityForScore(score) {
  if (!crossEngineFinite(score)) return "Unavailable";
  if (score < crossEngineParameters.severityBands.reviewScore) return "Review";
  if (score < crossEngineParameters.severityBands.watchScore) return "Watch";
  if (score < crossEngineParameters.severityBands.monitorScore) return "Monitor";
  return "Aligned";
}

function engineRunning(point, engine) {
  const config = crossEngineParameters.runningContext;
  const rpm = engineMetricValue(point, engine, "rpm");
  const fuelFlow = engineMetricValue(point, engine, "fuelFlow");
  const cht = engineMetricValue(point, engine, "cht");
  return (crossEngineFinite(rpm) && rpm > config.rpm)
    || (crossEngineFinite(fuelFlow) && fuelFlow > config.fuelFlowGph)
    || (crossEngineFinite(cht) && cht > config.chtF);
}

function comparisonPoint(point, left, right) {
  const phase = String(point?.phase ?? "");
  if (/shutdown/i.test(phase)) return false;
  return engineRunning(point, left) && engineRunning(point, right);
}

function matchedPowerPoint(point, left, right) {
  const config = crossEngineParameters.matchedPower;
  let matched = false;
  const leftRpm = engineMetricValue(point, left, "rpm");
  const rightRpm = engineMetricValue(point, right, "rpm");
  if (crossEngineFinite(leftRpm) && crossEngineFinite(rightRpm)) {
    if (Math.abs(rightRpm - leftRpm) > config.rpmDelta) return false;
    matched = true;
  }

  const leftMap = engineMetricValue(point, left, "map");
  const rightMap = engineMetricValue(point, right, "map");
  if (crossEngineFinite(leftMap) && crossEngineFinite(rightMap)) {
    if (Math.abs(rightMap - leftMap) > config.mapDeltaInHg) return false;
    matched = true;
  }

  return matched;
}

function pairedMetricSamples(data, engines, config) {
  const [left, right] = engines;
  return data
    .filter((point) => comparisonPoint(point, left, right))
    .filter((point) => !config.requireMatchedPower || matchedPowerPoint(point, left, right))
    .map((point) => {
      const leftValue = engineMetricValue(point, left, config.key);
      const rightValue = engineMetricValue(point, right, config.key);
      if (!crossEngineFinite(leftValue) || !crossEngineFinite(rightValue)) return null;
      return {
        elapsedSeconds: point.elapsedSeconds,
        phase: point.phase,
        leftValue,
        rightValue,
        delta: rightValue - leftValue,
      };
    })
    .filter(Boolean);
}

function sampleWindow(samples, label) {
  const times = samples.map((sample) => sample.elapsedSeconds).filter(crossEngineFinite);
  if (!times.length) return null;
  return {
    startTime: Math.min(...times),
    endTime: Math.max(...times),
    label,
    scope: "comparison",
  };
}

function directionText(delta, engines) {
  if (!crossEngineFinite(delta) || Math.abs(delta) < 0.0001) return "engine-to-engine";
  const [left, right] = engines;
  const higher = delta > 0 ? right : left;
  const lower = delta > 0 ? left : right;
  return `${higher.label} vs ${lower.label}`;
}

function comparisonSummary(config, value, delta, engines) {
  if (!crossEngineFinite(value)) return `${config.label} unavailable`;
  const direction = directionText(delta, engines);
  if (config.key === "fuelFlow") return `${direction}: ${formatNumber(value, config.unit, config.digits)} fuel-flow split at matched power`;
  if (config.key === "cht") return `${direction}: ${formatNumber(value, config.unit, config.digits)} CHT separation in matched flight phases`;
  return `${direction}: ${formatNumber(value, config.unit, config.digits)} ${config.label.toLowerCase()}`;
}

function metricCheck(data, engines, config, minSamples) {
  const samples = pairedMetricSamples(data, engines, config);
  if (samples.length < minSamples) {
    return {
      key: config.key,
      label: config.label,
      score: null,
      available: false,
      sampleCount: samples.length,
      limitation: config.requireMatchedPower
        ? `${config.label} needs ${minSamples} samples with matched RPM or MAP on both engines`
        : `${config.label} needs ${minSamples} paired samples on both engines`,
    };
  }

  const deltas = samples.map((sample) => sample.delta).filter(crossEngineFinite);
  const averageDelta = averageValues(deltas);
  const absDeltas = deltas.map(Math.abs);
  const value = config.mode === "p90" ? quantile(absDeltas, 0.9) : Math.abs(averageDelta);
  const score = scoreForDelta(value, config);
  return {
    key: config.key,
    label: config.label,
    score,
    severity: severityForScore(score),
    available: true,
    sampleCount: samples.length,
    value,
    averageDelta,
    unit: config.unit,
    digits: config.digits,
    summary: comparisonSummary(config, value, averageDelta, engines),
    window: sampleWindow(samples, config.label),
  };
}

function metricCoverage(data, engine, metric) {
  if (!data.length) return 0;
  const valid = data.filter((point) => crossEngineFinite(engineMetricValue(point, engine, metric))).length;
  return valid / data.length;
}

function dataQualityCheck(data, engines) {
  const [left, right] = engines;
  const config = crossEngineParameters.dataQuality;
  const gaps = crossEngineParameters.coverageMetrics
    .map((metric) => {
      const leftCoverage = metricCoverage(data, left, metric);
      const rightCoverage = metricCoverage(data, right, metric);
      const gap = Math.abs(rightCoverage - leftCoverage);
      return {
        metric,
        leftCoverage,
        rightCoverage,
        gap,
        higher: rightCoverage > leftCoverage ? right : left,
        lower: rightCoverage > leftCoverage ? left : right,
      };
    })
    .filter((item) => item.gap >= config.minCoverageGap && Math.max(item.leftCoverage, item.rightCoverage) >= config.minMaxCoverage)
    .sort((a, b) => b.gap - a.gap);

  if (!gaps.length) return null;
  const top = gaps[0];
  const score = top.gap >= config.reviewGap ? config.reviewScore : config.monitorScore;
  return {
    key: "dataQuality",
    label: "Data-quality asymmetry",
    score,
    severity: severityForScore(score),
    available: true,
    sampleCount: data.length,
    value: top.gap,
    averageDelta: top.rightCoverage - top.leftCoverage,
    unit: "%",
    digits: 0,
    summary: `${top.lower.label} has weaker ${top.metric} coverage than ${top.higher.label}`,
    limitation: `${top.lower.label} ${top.metric} coverage ${Math.round(Math.min(top.leftCoverage, top.rightCoverage) * 100)}% vs ${Math.round(Math.max(top.leftCoverage, top.rightCoverage) * 100)}%`,
    gaps,
  };
}

// Timing provenance of a separate-monitor twin flight for the cross-engine
// comparison. "verified": both engine offsets came from the automatic RPM match
// against a shared CGR-30C. "manual": every engine recording is present, none
// sits on an unconfirmed recorded clock, and the owner set at least one offset
// in Sync engine data. "unverified": recorded clocks only, or an engine
// recording is missing. null: the flight does not come from separate monitors.
export function cgrTwinTimingProvenance(alignment) {
  if (!alignment || typeof alignment !== "object") return null;
  if (alignment.verified === true) return "verified";
  const sources = Array.isArray(alignment.sources) ? alignment.sources : [];
  const primaries = sources.filter((source) => source?.role !== "shared");
  const present = ["left", "right"].every((role) => primaries.some((source) => source?.role === role));
  if (!present) return "unverified";
  if (primaries.some((source) => source?.method === "clock")) return "unverified";
  return primaries.some((source) => source?.method === "manual") ? "manual" : "unverified";
}

export function analyzeCrossEngineComparison(data = [], engines = [], options = {}) {
  const metric = computeCrossEngineComparison(data, engines, options);
  if (options.timingProvenance === "manual") {
    // Owner-set timing is a review input, not automatic proof of simultaneity.
    // Carry that on the metric so cards, reports and summaries can say so.
    metric.details = { ...(metric.details ?? {}), timing: "manual" };
  }
  return metric;
}

function computeCrossEngineComparison(data = [], engines = [], options = {}) {
  if (options.timingVerified === false) return {
    key: "engineDelta", score: null, note: "Separate monitor timing is not verified",
    details: { available: false, reason: "unverified-source-timing" },
  };
  if ((engines?.length ?? 0) < 2) {
    return {
      key: "engineDelta",
      score: null,
      note: "Single-engine log",
      details: { available: false, reason: "single-engine" },
    };
  }

  const comparedEngines = engines.slice(0, 2);
  const minSampleConfig = crossEngineParameters.minSamples;
  const minSamples = options.minSamples ?? Math.max(minSampleConfig.floor, Math.min(minSampleConfig.cap, Math.ceil(data.length * minSampleConfig.dataShare)));
  const checks = crossEngineParameters.comparisonMetrics.map((config) => metricCheck(data, comparedEngines, config, minSamples));
  const dataQuality = dataQualityCheck(data, comparedEngines);
  const scoredChecks = [...checks, dataQuality].filter((check) => crossEngineFinite(check?.score));
  const availableChecks = checks.filter((check) => check.available);

  if (!scoredChecks.length && !availableChecks.length) {
    return {
      key: "engineDelta",
      score: null,
      note: "Cross-engine comparison unavailable",
      details: {
        available: false,
        engines: comparedEngines,
        checks,
        limitations: checks.map((check) => check.limitation).filter(Boolean),
      },
    };
  }

  const primary = scoredChecks
    .slice()
    .sort((a, b) => a.score - b.score || (b.value ?? 0) - (a.value ?? 0))[0] ?? null;
  const score = clampScore(Math.min(...scoredChecks.map((check) => check.score)));
  const flagged = scoredChecks.filter((check) => check.score < crossEngineParameters.severityBands.monitorScore);
  const limitations = checks.filter((check) => !check.available && check.limitation).map((check) => check.limitation);
  const status = score < crossEngineParameters.severityBands.monitorScore ? primary.severity : "Aligned";
  return {
    key: "engineDelta",
    score,
    note: flagged.length ? primary.summary : "Cross-engine comparison aligned",
    details: {
      available: true,
      status,
      engines: comparedEngines.map((engine) => ({
        id: engine.id,
        side: engine.side,
        label: engine.label,
        shortLabel: engine.shortLabel,
      })),
      checks,
      dataQuality,
      primary,
      flagged,
      limitations,
    },
  };
}

export function crossEngineComparisonFinding(metric = {}) {
  const details = metric.details ?? {};
  const primary = details.primary;
  if (!primary || !crossEngineFinite(metric.score)) {
    return {
      title: "Cross-engine comparison unavailable",
      meta: "Required paired channels are missing",
      body: "The app could not compare the engines reliably because there were not enough paired samples across RPM, MAP, fuel flow, CHT, oil, or TIT channels.",
      causes: [["Missing or uneven engine-side channels", 0.7], ["Unsupported engine layout metadata", 0.3]],
      actions: [["medium", "Review Raw Data engine/channel mapping before using engine-to-engine balance cues"]],
      confidence: "Low",
      scope: "comparison",
    };
  }

  const flagged = details.flagged ?? [];
  // Owner-set timing (Sync engine data) is a review input, not confirmed
  // simultaneity: the review card, persisted finding and PDF say so and the
  // finding carries lower confidence. The severity label stays untouched so
  // the presentation layer's label mapping keeps working.
  const manualTiming = details.timing === "manual";
  const confidence = manualTiming || primary.key === "dataQuality" || details.limitations?.length ? "Low" : "Medium";
  const title = metric.score < crossEngineParameters.severityBands.monitorScore
    ? `Cross-engine ${primary.label.toLowerCase()}`
    : "Cross-engine comparison aligned";
  const evidence = flagged.length ? flagged.slice(0, 4) : [primary];
  const meta = primary.summary ?? metric.note;
  const body = metric.score < crossEngineParameters.severityBands.monitorScore
    ? `The cross-engine comparison found ${primary.summary}. Treat this as a comparison cue, not as proof that one engine has a mechanical fault. Mixture, throttle/prop synchronization, cowl flap position, installation differences, sensor calibration, and pilot technique can all create side-to-side differences.`
    : "The available side-to-side engine channels stayed close enough for this first-pass comparison. Keep using per-engine and aircraft-level cues for the rest of the review.";
  const manualTimingNote = "Engine timing for this flight was set by hand in Sync engine data, so treat the side-to-side comparison as a review input rather than confirmed simultaneity.";
  return {
    title,
    meta: manualTiming ? `${meta} · Manual timing, set in Sync engine data` : meta,
    body: manualTiming ? `${body} ${manualTimingNote}` : body,
    evidence: [
      ...evidence.map((check) => ({
        label: check.label,
        value: check.key === "dataQuality"
          ? check.limitation
          : formatNumber(check.value, check.unit, check.digits),
        detail: check.summary ?? check.limitation ?? `${check.sampleCount ?? 0} paired samples`,
      })),
      ...(manualTiming ? [{
        label: "Timing",
        value: "Manual",
        detail: "Engine offsets were set by hand in Sync engine data",
      }] : []),
    ],
    causes: [
      ["Pilot technique, mixture, throttle/prop synchronization, or cowl flap position", 0.34],
      ["Sensor calibration or channel mapping difference between engines", 0.24],
      ["Installation or normal side-to-side airflow difference", 0.2],
      ["Engine-specific condition if the same split repeats in comparable phases", 0.16],
      ["Data-quality limitation if one side has weaker coverage", 0.06],
    ],
    actions: [
      ["medium", "Compare both engines on matched RPM, MAP, fuel flow, CHT, oil, and phase before narrowing to one side"],
      ["medium", "Check whether the same side-to-side split repeats on nearby comparable flights"],
      ["medium", "Confirm mixture, cowl flap, throttle/prop synchronization, and fuel-flow calibration context"],
      ["low", "Treat comparison cues as lower confidence when required channels are missing or asymmetric"],
    ],
    confidence,
    tone: "cue",
    statusLabel: primary.severity ?? "Compare",
    scope: "comparison",
  };
}
